import PropTypes from 'prop-types';
import React from 'react';
import {
  BROWSE_MORE_JOBS_CITY_LIMIT,
  getFindCountryBySlug,
} from 'constants/findJobs';
import {
  getPopularJobCountriesShort,
  POPULAR_JOB_ROLES,
} from 'constants/popularJobLinks';
import { toSeoUrl } from 'helpers/HelperFunctions';
import styles from './BrowseMoreJobs.module.scss';

/**
 * Guest-only SEO link box for popular roles and locations.
 * Static markup from constants — safe for SSR (no effects or fetches).
 * Mounted in the desktop filter column and in the job list for mobile;
 * CSS shows only one at a time.
 *
 * When `country` matches a FIND_COUNTRIES_WITH_CITIES slug/name
 * (e.g. united-states), role links include that country and the location
 * list becomes the first cities for that country instead of the popular
 * country set.
 */
const BrowseMoreJobs = ({ placement, country = null }) => {
  const countryData = getFindCountryBySlug(country);
  const countrySlug = countryData
    ? countryData.slug
    : country
    ? toSeoUrl(country)
    : null;
  const countryPath = countrySlug ? `/in/${countrySlug}` : '';

  const cityLinks = countryData
    ? countryData.cities.slice(0, BROWSE_MORE_JOBS_CITY_LIMIT).map((city) => ({
        key: city.slug,
        href: `/jobs/in/${countryData.slug}/${city.slug}`,
        label: `Jobs in ${city.label}`,
      }))
    : null;

  const countryLinks = getPopularJobCountriesShort();

  return (
    <aside
      className={`${styles.box} ${styles[placement]}`}
      data-testid="browse-more-jobs"
      data-placement={placement}
      data-country={countrySlug || undefined}
      aria-label="Browse more jobs"
    >
      <h3 className={styles.title}>Browse more jobs</h3>
      <ul className={styles.list}>
        {POPULAR_JOB_ROLES.map((role) => (
          <li key={role.slug}>
            <a href={`/jobs/${role.slug}${countryPath}`}>{role.footerLabel}</a>
          </li>
        ))}
        {cityLinks &&
          cityLinks.map((link) => (
            <li key={link.key}>
              <a href={link.href}>{link.label}</a>
            </li>
          ))}
      </ul>
      {!cityLinks && (
        <p className={styles.countriesLine}>
          Jobs in{' '}
          {countryLinks.map((entry, index) => (
            <React.Fragment key={entry.slug}>
              {index > 0 && <span aria-hidden="true"> · </span>}
              <a href={`/jobs/in/${entry.slug}`}>{entry.shortLabel}</a>
            </React.Fragment>
          ))}
          <span aria-hidden="true"> · </span>
          <a href="/jobs">Worldwide</a>
        </p>
      )}
      <a href="/jobs/categories" className={styles.seeAll}>
        See all job categories
      </a>
    </aside>
  );
};

BrowseMoreJobs.propTypes = {
  placement: PropTypes.oneOf(['desktop', 'mobile']).isRequired,
  /** SEO country slug or display name from the jobs board filter / URL */
  country: PropTypes.string,
};

export default BrowseMoreJobs;
